// client/src/components/billingTransactions/TransactionDetails.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Breadcrumbs,
  Link,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import billingTransactionService from '../../services/billingTransactionService';
import paymentBylawService from '../../services/paymentBylawService';

const TransactionDetails = () => {
  const { t, i18n } = useTranslation();
  const { id: bylawId, transactionId } = useParams();
  const navigate = useNavigate();
  const { direction } = useContext(LocaleContext);
  
  const [transaction, setTransaction] = useState(null);
  const [bylaw, setBylaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Current language
  const lang = i18n.language;
  
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load bylaw details
        const bylawResponse = await paymentBylawService.getPaymentBylaw(bylawId);
        setBylaw(bylawResponse.data);
        
        // Load transaction details
        const transactionResponse = await billingTransactionService.getBillingTransaction(transactionId);
        setTransaction(transactionResponse.data);
        
      } catch (err) {
        setError(t('errorLoadingTransactionDetails'));
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [bylawId, transactionId, t]);
  
  // Format currency
  const formatCurrency = (value) => {
    if (!value) return '';
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };
  
  // Calculate total distribution value
  const calculateTotalDistribution = () => {
    if (!transaction || !transaction.distributions) return 0;
    return transaction.distributions.reduce(
      (total, dist) => total + parseFloat(dist.value || 0), 
      0
    );
  };
  
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }
  
  if (!transaction) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">{t('transactionNotFound')}</Alert>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator="›" aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/payment-bylaws" underline="hover" color="inherit">
          {t('paymentBylaws')}
        </Link>
        <Link 
          component={RouterLink} 
          to={`/payment-bylaws/${bylawId}/transactions`} 
          underline="hover" 
          color="inherit"
        >
          {bylaw?.name || t('bylawTransactions')}
        </Link>
        <Typography color="text.primary">
          {transaction.paymentItem?.name || t('transactionDetails')}
        </Typography>
      </Breadcrumbs>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('transactionDetails')}
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">{t('paymentBylaw')}:</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {bylaw?.name || '-'}
            </Typography>
            
            <Typography variant="subtitle1">{t('paymentItem')}:</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {transaction.paymentItem?.name || '-'}
            </Typography>
            
            <Typography variant="subtitle1">{t('billingDetail')}:</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {transaction.billingDetail ? (
                <>
                  {transaction.billingDetail.name}
                  <Chip 
                    size="small" 
                    label={t(transaction.billingDetail.detail_type)} 
                    color={transaction.billingDetail.detail_type === 'university' ? 'primary' : 'secondary'}
                    sx={{ ml: 1 }}
                  />
                  {transaction.billingDetail.faculty && 
                    ` - ${transaction.billingDetail.faculty[`name_${lang}`]}`}
                </>
              ) : (
                t('noBillingDetail')
              )}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">{t('totalValue')}:</Typography>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
              {formatCurrency(transaction.total_value)}
            </Typography>
            
            <Typography variant="subtitle1">{t('createdBy')}:</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {transaction.creator?.username || '-'}
              {transaction.created_at && ` • ${new Date(transaction.created_at).toLocaleDateString()}`}
            </Typography>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="h6" gutterBottom>
          {t('valueDistribution')}
        </Typography>
        
        {transaction.distributions && transaction.distributions.length > 0 ? (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('bankAccount')}</TableCell>
                  <TableCell>{t('accountNumber')}</TableCell>
                  <TableCell align="right">{t('value')}</TableCell>
                  <TableCell align="right">{t('percentage')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transaction.distributions.map((distribution) => (
                  <TableRow key={distribution.id}>
                    <TableCell>{distribution.bankAccount?.name || '-'}</TableCell>
                    <TableCell>{distribution.bankAccount?.account_number || '-'}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(distribution.value)}
                    </TableCell>
                    <TableCell align="right">
                      {transaction.total_value 
                        ? ((parseFloat(distribution.value) / parseFloat(transaction.total_value)) * 100).toFixed(2) + '%' 
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>
                    {t('total')}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(calculateTotalDistribution())}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {transaction.total_value && Math.abs(calculateTotalDistribution() - parseFloat(transaction.total_value)) <= 0.01
                      ? '100%'
                      : (calculateTotalDistribution() / parseFloat(transaction.total_value) * 100).toFixed(2) + '%'}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('noDistributionsFound')}
          </Alert>
        )}
        
        {/* Verify if distribution total matches transaction total */}
        {transaction.total_value && transaction.distributions && transaction.distributions.length > 0 && (
          <Box sx={{ mb: 3 }}>
            {Math.abs(calculateTotalDistribution() - parseFloat(transaction.total_value)) > 0.01 ? (
              <Alert severity="warning">
                {t('distributionTotalMismatch', {
                  distributionTotal: formatCurrency(calculateTotalDistribution()),
                  totalValue: formatCurrency(transaction.total_value)
                })}
              </Alert>
            ) : (
              <Alert severity="success">
                {t('distributionTotalMatches')}
              </Alert>
            )}
          </Box>
        )}
        
        <Box sx={{ 
          mt: 3, 
          display: 'flex',
          gap: 2,
          justifyContent: direction === 'ltr' ? 'flex-end' : 'flex-start'
        }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/payment-bylaws/${bylawId}/transactions`)}
          >
            {t('back')}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default TransactionDetails;